import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Upload, Calendar, Sparkles, TrendingUp, Loader2, X } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const CONTENT_TYPES = ['affirmation', 'insight', 'deep_work', 'meditation', 'breathing'];

export const AdminCmsTab = ({ token }) => {
  const [cmsStats, setCmsStats] = useState(null);
  const [cmsContent, setCmsContent] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState("affirmation");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContent, setNewContent] = useState({
    title: "", content: "", day_number: "", week_number: "", month_number: "", is_premium: false
  });
  const [isAddingContent, setIsAddingContent] = useState(false);
  const [bulkUploadText, setBulkUploadText] = useState("");
  const [isUploadingBulk, setIsUploadingBulk] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchCmsStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    fetchCmsContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on type change only
  }, [selectedContentType]);

  const fetchCmsStats = async () => {
    try {
      const response = await axios.get(`${API}/cms/stats`, authHeaders);
      setCmsStats(response.data);
    } catch (error) {
      console.error("Error fetching CMS stats:", error);
    }
  };

  const fetchCmsContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/content/list/${selectedContentType}`, authHeaders);
      setCmsContent(response.data.items || []);
    } catch (error) {
      console.error("Error fetching CMS content:", error);
    }
  };

  const handleAddContent = async () => {
    if (!newContent.title.trim() || !newContent.content.trim()) {
      toast.error("Please fill in title and content");
      return;
    }
    setIsAddingContent(true);
    try {
      const payload = {
        content_type: selectedContentType,
        title: newContent.title,
        content: newContent.content,
        day_number: newContent.day_number ? parseInt(newContent.day_number) : null,
        week_number: newContent.week_number ? parseInt(newContent.week_number) : null,
        month_number: newContent.month_number ? parseInt(newContent.month_number) : null,
        is_premium: newContent.is_premium
      };
      await axios.post(`${API}/cms/content/add`, payload, authHeaders);
      toast.success("Content added successfully!");
      setShowAddForm(false);
      setNewContent({ title: "", content: "", day_number: "", week_number: "", month_number: "", is_premium: false });
      fetchCmsContent();
      fetchCmsStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add content");
    } finally {
      setIsAddingContent(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadText.trim()) {
      toast.error("Please enter content to upload");
      return;
    }
    setIsUploadingBulk(true);
    try {
      const items = JSON.parse(bulkUploadText);
      if (!Array.isArray(items)) {
        throw new Error("Input must be a JSON array");
      }
      const response = await axios.post(`${API}/cms/content/bulk-upload`, {
        content_type: selectedContentType,
        items: items
      }, authHeaders);
      toast.success(`Uploaded ${response.data.added} new, updated ${response.data.updated} existing`);
      setBulkUploadText("");
      fetchCmsContent();
      fetchCmsStats();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format. Please check your input.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to upload content");
      }
    } finally {
      setIsUploadingBulk(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm("Are you sure you want to delete this content?")) return;
    try {
      await axios.delete(`${API}/cms/content/${contentId}`, authHeaders);
      toast.success("Content deleted");
      fetchCmsContent();
      fetchCmsStats();
    } catch (error) {
      toast.error("Failed to delete content");
    }
  };

  return (
    <>
      {/* CMS Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {cmsStats?.stats && Object.entries(cmsStats.stats).map(([key, stat]) => (
          <Card key={key} className="glass-card rounded-2xl border-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium capitalize">{key.replace('_', ' ')}</span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {stat.count}
                {stat.target && <span className="text-sm font-normal text-muted-foreground">/{stat.target}</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{stat.rotation} rotation</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Current Rotation Info */}
      {cmsStats?.current_rotation && (
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <span>Day {cmsStats.current_rotation.day_of_year} of 365</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span>Week {cmsStats.current_rotation.week_of_year}</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span>Month {cmsStats.current_rotation.month}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content Type Selector */}
      <div className="flex gap-2 mb-6">
        {CONTENT_TYPES.map(type => (
          <Button
            key={type}
            variant={selectedContentType === type ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedContentType(type)}
            className="capitalize rounded-lg"
          >
            {type.replace('_', ' ')}
          </Button>
        ))}
      </div>

      {/* Add Button */}
      <div className="flex gap-4 mb-6">
        <Button onClick={() => setShowAddForm(!showAddForm)} className="rounded-xl">
          <FileText className="w-4 h-4 mr-2" />
          Add Single Item
        </Button>
      </div>

      {/* Add Single Content Form */}
      {showAddForm && (
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Add New {selectedContentType.replace('_', ' ')}</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Title</label>
                <Input
                  placeholder="Enter title..."
                  value={newContent.title}
                  onChange={(e) => setNewContent(prev => ({ ...prev, title: e.target.value }))}
                  className="rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Content</label>
                <Textarea
                  placeholder="Enter content..."
                  value={newContent.content}
                  onChange={(e) => setNewContent(prev => ({ ...prev, content: e.target.value }))}
                  className="rounded-lg min-h-[100px]"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                {selectedContentType === 'affirmation' && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">Day Number (1-365)</label>
                    <Input
                      type="number" min="1" max="365" placeholder="Day #"
                      value={newContent.day_number}
                      onChange={(e) => setNewContent(prev => ({ ...prev, day_number: e.target.value }))}
                      className="rounded-lg"
                    />
                  </div>
                )}
                {selectedContentType === 'insight' && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">Week Number (1-52)</label>
                    <Input
                      type="number" min="1" max="52" placeholder="Week #"
                      value={newContent.week_number}
                      onChange={(e) => setNewContent(prev => ({ ...prev, week_number: e.target.value }))}
                      className="rounded-lg"
                    />
                  </div>
                )}
                {selectedContentType === 'deep_work' && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">Month Number (1-12)</label>
                    <Input
                      type="number" min="1" max="12" placeholder="Month #"
                      value={newContent.month_number}
                      onChange={(e) => setNewContent(prev => ({ ...prev, month_number: e.target.value }))}
                      className="rounded-lg"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_premium"
                  checked={newContent.is_premium}
                  onChange={(e) => setNewContent(prev => ({ ...prev, is_premium: e.target.checked }))}
                />
                <label htmlFor="is_premium" className="text-sm">Premium only</label>
              </div>
              <Button onClick={handleAddContent} disabled={isAddingContent} className="rounded-xl">
                {isAddingContent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Add Content
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Upload Section */}
      <Card className="glass-card rounded-2xl border-0 mb-6">
        <CardContent className="p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">
            <Upload className="w-5 h-5 inline mr-2" />
            Bulk Upload
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Paste JSON array. Example format:
            <code className="block bg-muted p-2 rounded mt-2 text-xs">
              {`[{"title": "Affirmation 1", "content": "I am worthy...", "day_number": 1}, ...]`}
            </code>
          </p>
          <Textarea
            placeholder='[{"title": "...", "content": "...", "day_number": 1}]'
            value={bulkUploadText}
            onChange={(e) => setBulkUploadText(e.target.value)}
            className="rounded-lg min-h-[150px] font-mono text-sm mb-4"
          />
          <Button onClick={handleBulkUpload} disabled={isUploadingBulk || !bulkUploadText.trim()} className="rounded-xl">
            {isUploadingBulk ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload {selectedContentType.replace('_', ' ')}s
          </Button>
        </CardContent>
      </Card>

      {/* Content List */}
      <Card className="glass-card rounded-2xl border-0">
        <CardContent className="p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">
            {selectedContentType.replace('_', ' ')} Content ({cmsContent.length})
          </h3>

          {cmsContent.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No {selectedContentType.replace('_', ' ')} content yet. Add some above!</p>
          ) : (
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {cmsContent.map((item, idx) => (
                <div key={item.id || idx} className="bg-muted/30 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                          {item.day_number ? `Day ${item.day_number}` :
                           item.week_number ? `Week ${item.week_number}` :
                           item.month_number ? `Month ${item.month_number}` : 'No slot'}
                        </span>
                        {item.is_premium && (
                          <span className="text-xs bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded">Premium</span>
                        )}
                      </div>
                      <h4 className="font-medium text-foreground">{item.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.content}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteContent(item.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
